/**
 * Population requirement for a control's test runs.
 *
 * Stored on the control's `sampling_attributes` JSON (no separate column):
 *   population_required          true  -> test runs need a population file (default)
 *                                false -> samples are the evidence files the auditor uploads
 *   population_rationale         AI explanation
 *   population_confidence        'low' | 'medium' | 'high'
 *   population_required_source   'ai' | 'auditor'  (auditor overrides win over later AI runs)
 *   population_required_set_by   display name of the auditor who overrode it
 *   population_required_set_at   ISO timestamp of the override
 */

export type PopulationStatus = 'provided' | 'not_required';

export type PopulationRequirement = {
  required: boolean;
  rationale: string | null;
  confidence: 'low' | 'medium' | 'high' | null;
  source: 'ai' | 'auditor' | null;
  setBy: string | null;
  setAt: string | null;
  /** True when the control has never been assessed (treated as required). */
  assessed: boolean;
};

type SamplingAttributes = Record<string, unknown>;

function asAttrs(value: unknown): SamplingAttributes {
  return value && typeof value === 'object' && !Array.isArray(value) ? (value as SamplingAttributes) : {};
}

export function getPopulationRequirement(control: { sampling_attributes?: unknown } | null | undefined): PopulationRequirement {
  const attrs = asAttrs(control?.sampling_attributes);
  const flag = attrs.population_required;
  const confidence = attrs.population_confidence;
  const source = attrs.population_required_source;
  return {
    required: typeof flag === 'boolean' ? flag : true,
    rationale: typeof attrs.population_rationale === 'string' && attrs.population_rationale ? attrs.population_rationale : null,
    confidence: confidence === 'low' || confidence === 'medium' || confidence === 'high' ? confidence : null,
    source: source === 'ai' || source === 'auditor' ? source : null,
    setBy: typeof attrs.population_required_set_by === 'string' ? attrs.population_required_set_by : null,
    setAt: typeof attrs.population_required_set_at === 'string' ? attrs.population_required_set_at : null,
    assessed: typeof flag === 'boolean',
  };
}

/** Population status a new run should start with for this control. */
export function populationStatusForControl(control: { sampling_attributes?: unknown } | null | undefined): PopulationStatus {
  return getPopulationRequirement(control).required ? 'provided' : 'not_required';
}

/**
 * Merge the AI's population decision (from a generated control spec) into the control's
 * existing sampling_attributes. Returns null when there is nothing to save: the AI gave
 * no decision, or the auditor already overrode it (the auditor's choice is kept).
 */
export function mergeGeneratedPopulationRequirement(
  existing: unknown,
  generated: { sampling_attributes?: unknown } | null | undefined,
): SamplingAttributes | null {
  const gen = asAttrs(generated?.sampling_attributes);
  if (typeof gen.population_required !== 'boolean') return null;
  const current = asAttrs(existing);
  if (current.population_required_source === 'auditor') return null;
  return {
    ...current,
    population_required: gen.population_required,
    population_rationale: typeof gen.population_rationale === 'string' ? gen.population_rationale : null,
    population_confidence: typeof gen.population_confidence === 'string' ? gen.population_confidence : null,
    population_required_source: 'ai',
    population_required_set_by: null,
    population_required_set_at: null,
  };
}

/** sampling_attributes after the auditor sets the flag by hand. */
export function withAuditorPopulationOverride(
  existing: unknown,
  required: boolean,
  setBy?: string | null,
): SamplingAttributes {
  return {
    ...asAttrs(existing),
    population_required: required,
    population_required_source: 'auditor',
    population_required_set_by: setBy || null,
    population_required_set_at: new Date().toISOString(),
  };
}
